import React from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import QuestsPage from './pages/QuestsPage';
import ConnectionsPage from './pages/ConnectionsPage';
import CommunityDetailPage from './pages/CommunityDetailPage';
import CourseDetailPage from './pages/CourseDetailPage';
import MyCommunityPage from './pages/MyCommunityPage';
import MemberByEmailPage from './pages/MemberByEmailPage';
import MyTicketsPage from './pages/MyTicketsPage';
import MessagesPage from './pages/MessagesPage';
import SearchPage from './pages/SearchPage';
import SettingsPage from './pages/SettingsPage';
import SsoExchangePage from './pages/SsoExchangePage';
import RoadmapPage from './pages/RoadmapPage';
import MarketplacePage from './pages/MarketplacePage';
import MarketplaceDetailPage from './pages/MarketplaceDetailPage';

// Auth components
import Login from './components/auth/Login';
import Register from './components/auth/Register';

// Dashboard components
import DashboardHome from './components/dashboard/DashboardHome';
import Communities from './components/dashboard/Communities';
import Courses from './components/dashboard/Courses';
import Events from './components/dashboard/Events';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/sso/exchange" element={<SsoExchangePage />} />
      
      {/* Auth routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Protected dashboard routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardHome />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/communities" element={
        <ProtectedRoute>
          <Communities />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/community/:id" element={
        <ProtectedRoute>
          <CommunityDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/my-community" element={
        <ProtectedRoute>
          <MyCommunityPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/members/by-email/:email" element={
        <ProtectedRoute>
          <MemberByEmailPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/my-tickets" element={
        <ProtectedRoute>
          <MyTicketsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/search" element={
        <ProtectedRoute>
          <SearchPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/roadmap" element={
        <ProtectedRoute>
          <RoadmapPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/marketplace" element={
        <ProtectedRoute>
          <MarketplacePage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/marketplace/:id" element={
        <ProtectedRoute>
          <MarketplaceDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/courses" element={
        <ProtectedRoute>
          <Courses />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/courses/:id" element={
        <ProtectedRoute>
          <CourseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/events" element={
        <ProtectedRoute>
          <Events />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/quests" element={
        <ProtectedRoute>
          <QuestsPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/connections" element={
        <ProtectedRoute>
          <ConnectionsPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
